import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorBanner from '../components/ErrorBanner';

export default function CityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [city, setCity] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/cities/${id}`, {});
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Error ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setCity(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}&cityId=${id}`);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading city..." />;
  }

  if (error || !city) {
    return <ErrorBanner message={error || 'City not found'} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-semibold">{city.name}</h1>
        {city.state && (
          <p className="text-lg text-slate-600 dark:text-slate-400">
            {city.state} {city.zipCode && `• ${city.zipCode}`}
          </p>
        )}
        <p className="text-slate-500 dark:text-slate-500">
          {city.garageSellerCount} {city.garageSellerCount === 1 ? 'garage sale' : 'garage sales'}
        </p>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="w-full">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search items in this city..."
            className="w-full h-12 text-base px-4 pr-14 rounded-full border-0 bg-gray-100 text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
          <button
            type="submit"
            className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-orange-500 hover:bg-orange-600 active:bg-orange-700 transition-colors flex items-center justify-center"
            aria-label="Search"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>
        </div>
      </form>

      {city.garageSellers.length === 0 ? (
        <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-8 text-center">
          <p className="text-xl text-slate-600 dark:text-slate-400">
            No garage sales in this city yet.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {city.garageSellers.map((garageSeller) => (
            <Link
              key={garageSeller.id}
              to={`/vendor/${garageSeller.id}`}
              className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden cursor-pointer transition-all duration-200 hover:border-indigo-500 dark:hover:border-indigo-400 hover:shadow-lg"
            >
              <div className="p-6 flex flex-col gap-4">
                <h2 className="text-2xl font-semibold text-center">
                  {garageSeller.storeName ? `${garageSeller.storeName} Garage sale` : 'Garage Sale'}
                </h2>
                {garageSeller.address && (
                  <p className="text-slate-600 dark:text-slate-400 flex items-center gap-2">
                    <span className="text-slate-400 dark:text-slate-500" aria-hidden>📍</span>
                    {garageSeller.address}
                  </p>
                )}
                {garageSeller.phone && (
                  <p className="text-slate-600 dark:text-slate-400 flex items-center gap-2">
                    <span className="text-slate-400 dark:text-slate-500" aria-hidden>📞</span>
                    {garageSeller.phone}
                  </p>
                )}
                <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                  <p className="text-sm text-indigo-600 dark:text-indigo-400 font-medium text-center">
                    View garage sale →
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
